import { FC } from 'react';
import { Form } from 'antd';
import {
  clearListFilters,
  formatArrangedData,
  getArrangesInitialValues,
  getParsedSelectedItems,
  getSelectedItemsLabels,
} from '../../../../lib/utils/arranges-helpers';
import { useAppSelector } from '../../../../lib/store/hooks';
import { TENANT_COLUMN } from '../../helpers/constants';
import { useStringArrange } from '../../../../features/table/helpers/useStringArrange';
import { useTenantArrangeRequests } from '../../hooks/useTenantArrangeRequests';
import { useArrangeSubmit } from '../../../../features/table/helpers/useArrangeSubmit';
import { TableHeaderCell } from '../../../../components/Table/TableHeaderCell/TableHeaderCell';
import { ArrangeListPopup } from '../../../../features/table/components/popups/ArrangeListPopup';
import { useTranslation } from 'react-i18next';
import { useLazyGetUsersForFilteringQuery } from '../../../../lib/store/api/usersManagement';
import { User } from '../../../userManagement/helpers/constants';

const arrangedId = TENANT_COLUMN.CREATED_BY;
const searchFieldName = 'searchByLogin';
const filterFieldName = 'includeCreatedBy';

const columns = 'shared.columns';

type Props = {
  className?: string;
};

export const TenantCreatorHeaderCell: FC<Props> = ({ className }) => {
  const { t } = useTranslation();

  const {
    arrangeId,
    sorting,
    filtering: { filter },
  } = useAppSelector(({ tenants }) => tenants);
  const isActiveArrange = arrangeId === arrangedId;
  const [form] = Form.useForm();
  const getSelectedItems = () => getSelectedItemsLabels(form);
  const [getUsers] = useLazyGetUsersForFilteringQuery();

  const { filtering } = useStringArrange({
    form,
    request: (value: string) => {
      return getUsers({ search: value, searchFieldName }).unwrap();
    },
    formatResponse: (data: { content: any[] }) => formatArrangedData(data, User.USERNAME),
  });

  const clearFilters = () => {
    clearListFilters(form);
    filtering.clearOptions();
  };

  const { applyArranges, removeFilter, removeFilterValue } = useTenantArrangeRequests({
    arrangedId,
    clearFilters,
    getFilterLabel: () => getSelectedItems().join(', '),
    getFilters: () => ({ [filterFieldName]: getSelectedItems() }),
    form,
    filterFieldName,
  });

  const headerCellProps = {
    arrangedId,
    title: t(`${columns}.creator`),
    removeFilter,
    filterLabel: Array.isArray(filter) && filter.join(', '),
    isActiveArrange,
    form,
    applyArranges,
    getParsedItems: () => getParsedSelectedItems(form),
    removeFilterValue,
    sortOrder: sorting.sortOrder,
    rootProps: { className },
  };

  const { onFinish } = useArrangeSubmit(applyArranges);

  return (
    <TableHeaderCell {...headerCellProps}>
      <ArrangeListPopup
        initialValues={getArrangesInitialValues(form)}
        form={form}
        filtering={filtering}
        onFinish={onFinish}
        isActiveArrange={isActiveArrange}
      />
    </TableHeaderCell>
  );
};
